import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Search, MapPin, Briefcase, MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { toast } from 'sonner';
import ConnectButton from '../components/ConnectButton';

const Directory = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [search, users]);

  const fetchUsers = async () => {
    try {
      const { data } = await api.get('/users/directory');
      setUsers(data);
      setFilteredUsers(data);
    } catch (error) {
      toast.error('Failed to load directory');
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = [...users];

    if (search) {
      filtered = filtered.filter(
        (u) =>
          u.name?.toLowerCase().includes(search.toLowerCase()) ||
          u.current_role?.toLowerCase().includes(search.toLowerCase()) ||
          u.industry?.toLowerCase().includes(search.toLowerCase()) ||
          u.city?.toLowerCase().includes(search.toLowerCase())
      );
    }

    setFilteredUsers(filtered);
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-12 gap-6">
          <div className="hidden lg:block lg:col-span-3 sticky top-24 h-fit">
            <Sidebar />
          </div>

          <div className="col-span-12 lg:col-span-9" data-testid="directory-container">
            <div className="mb-6">
              <h1 className="font-oswald text-3xl font-bold text-[#1E3A8A] uppercase tracking-tight mb-2">
                Member Directory
              </h1>
              <p className="text-slate-600">Connect with fellow GBAians</p>
            </div>

            {/* Search and Filters */}
            <div className="bg-white rounded-md border border-slate-100 shadow-sm p-4 mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search members..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>

            {/* Members Grid */}
            {loading ? (
              <div className="text-center py-12">
                <p className="text-slate-500">Loading members...</p>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="bg-white rounded-md p-12 text-center">
                <p className="text-slate-500">No members found</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredUsers.map((member) => (
                  <Card
                    key={member.id}
                    className="p-6 bg-white border border-slate-100 shadow-sm hover:shadow-md transition-all cursor-pointer"
                    onClick={() => navigate(`/profile/${member.id}`)}
                    data-testid="member-card"
                  >
                    <div className="flex flex-col items-center text-center">
                      <Avatar className="h-20 w-20 mb-3">
                        <AvatarImage src={member.profile_photo} />
                        <AvatarFallback className="bg-[#1E3A8A] text-white text-xl">
                          {getInitials(member.name)}
                        </AvatarFallback>
                      </Avatar>

                      <h3 className="font-semibold text-lg text-slate-900 mb-1">
                        {member.name}
                      </h3>

                      <p className="text-sm text-slate-600 mb-3">GBA Alumni</p>

                      {member.current_role && (
                        <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                          <Briefcase className="h-4 w-4" />
                          <span className="line-clamp-1">{member.current_role}</span>
                        </div>
                      )}

                      {member.city && (
                        <div className="flex items-center gap-2 text-sm text-slate-600 mb-4">
                          <MapPin className="h-4 w-4" />
                          <span>{member.city}</span>
                        </div>
                      )}

                      <ConnectButton userId={member.id} size="sm" className="w-full" />
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Directory;
